import { supabase } from '@/utils/supabase/client';
import { functionsBaseUrl, hasSupabaseConfig, publicAnonKey } from '/utils/supabase/info';

export type AqlAssistantPage = 'dashboard' | 'alerts' | 'analyses' | 'growth' | 'camera_live';
export type AqlAssistantTimeWindow = '24h' | '7d' | '30d' | '90d';
export type AqlAssistantSeverity = 'info' | 'success' | 'warning' | 'critical';

export interface AqlAssistantContext {
  client_id: string | null;
  project_id: string | null;
  location_id: string | null;
  zone_id: string | null;
  kit_id: string | null;
  camera_id: string | null;
  species_id: string | null;
  phase_id: string | null;
  cohort_id: string | null;
  time_window: AqlAssistantTimeWindow;
  current_page: AqlAssistantPage;
  client_name?: string | null;
  project_name?: string | null;
  location_label?: string | null;
  zone_label?: string | null;
  kit_label?: string | null;
  camera_label?: string | null;
  species_label?: string | null;
  phase_label?: string | null;
  cohort_label?: string | null;
  language?: string | null;
}

export interface AqlAssistantCard {
  title: string;
  value: string;
  severity: AqlAssistantSeverity;
  explanation: string;
}

export interface AqlAssistantEvidence {
  label: string;
  table: string;
  record_id: string | null;
  timestamp: string | null;
}

export interface AqlAssistantResponse {
  answer: string;
  cards: AqlAssistantCard[];
  evidence: AqlAssistantEvidence[];
  suggested_questions: string[];
  usage?: AqlAssistantUsage;
}

export interface AqlAssistantUsage {
  provider: 'gemini' | 'fallback' | string;
  model: string;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  budget_tokens: number;
}

const parseAssistantResponse = async (response: Response) => {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { error: text };
  }
};

const assistantErrorMessage = (detail: string) => {
  if (/Authenticated Supabase session required|No API key found|JWT|auth|session/i.test(detail)) {
    return 'A sessão do assistente não está válida. Faz refresh ou entra novamente para renovar a autenticação.';
  }
  if (/Failed to fetch|Load failed|NetworkError|fetch/i.test(detail)) {
    return 'Não consegui chegar ao backend do assistente. Confirma a ligação e tenta novamente.';
  }
  return detail;
};

const looksLikeJwt = (value?: string | null) => {
  const token = String(value ?? "").trim();
  return /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(token);
};

export async function sendAqlAssistantQuestion(params: {
  question: string;
  context: AqlAssistantContext;
  authToken?: string | null;
}): Promise<AqlAssistantResponse> {
  const question = params.question.trim();
  if (!question) {
    throw new Error('Question is required.');
  }

  if (!hasSupabaseConfig) {
    throw new Error('Supabase is not configured for this build.');
  }

  const sessionResult = await supabase.auth.getSession().catch(() => ({ data: { session: null } }));
  const accessToken = sessionResult.data.session?.access_token
    ?? (looksLikeJwt(params.authToken) ? params.authToken : null);

  if (!accessToken) {
    throw new Error('A sessão do assistente não está válida. Faz refresh ou entra novamente para renovar a autenticação.');
  }

  let response: Response;
  try {
    response = await fetch(`${functionsBaseUrl}/aql-assistant-chat`, {
      method: 'POST',
      headers: {
        apikey: publicAnonKey,
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        ...(params.authToken ? { 'X-Session-Token': params.authToken } : {})
      },
      body: JSON.stringify({
        question,
        context: params.context
      })
    });
  } catch (err) {
    const detail = err instanceof Error ? err.message : 'Load failed';
    throw new Error(assistantErrorMessage(detail));
  }

  const payload: any = await parseAssistantResponse(response);
  if (!response.ok) {
    const detail = typeof payload?.error === 'string'
      ? payload.error
      : typeof payload?.message === 'string'
        ? payload.message
        : `${response.status}: ${response.statusText || 'Assistant request failed.'}`;
    throw new Error(assistantErrorMessage(detail));
  }

  return {
    answer: typeof payload.answer === 'string' ? payload.answer : '',
    cards: Array.isArray(payload.cards) ? payload.cards : [],
    evidence: Array.isArray(payload.evidence) ? payload.evidence : [],
    suggested_questions: Array.isArray(payload.suggested_questions) ? payload.suggested_questions : [],
    usage: payload.usage && typeof payload.usage === 'object'
      ? {
          provider: String(payload.usage.provider ?? 'gemini'),
          model: String(payload.usage.model ?? ''),
          prompt_tokens: Number(payload.usage.prompt_tokens ?? 0),
          completion_tokens: Number(payload.usage.completion_tokens ?? 0),
          total_tokens: Number(payload.usage.total_tokens ?? 0),
          budget_tokens: Number(payload.usage.budget_tokens ?? 1_000_000)
        }
      : undefined
  };
}
